import { useOutletContext } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectImplementationApi } from '../../../api/projectWorkspace';
import { ApiError } from '../../../api/client';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { Project, ProjectImplementation } from '../../../types/projects';
import { CommentThread } from '../../../components/CommentThread';

export function ProjectImplementationTab() {
  const { project } = useOutletContext<{ project: Project }>();
  const canManage = usePermission('project_implementation.manage');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({ queryKey: ['project-implementation', project.id], queryFn: () => projectImplementationApi.get(project.id) });
  const { register, handleSubmit } = useForm<Partial<ProjectImplementation>>({ values: query.data?.data });

  const saveMutation = useMutation({
    mutationFn: (values: Partial<ProjectImplementation>) => projectImplementationApi.update(project.id, values),
    onSuccess: (result) => { showSuccess('Implementation details saved.'); queryClient.setQueryData(['project-implementation', project.id], { data: result.data }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  if (query.isLoading) return <p className="muted">Loading…</p>;

  return (
    <form onSubmit={handleSubmit((values) => saveMutation.mutate(values))}>
      <section className="card">
        <h2>Implementation Approach</h2>
        <label>Approach<textarea rows={3} {...register('implementation_approach')} disabled={!canManage} /></label>
        <label>Arrangements<textarea rows={3} {...register('implementation_arrangements')} disabled={!canManage} /></label>
        <CommentThread projectId={project.id} entityType="implementation" />
      </section>
      <section className="card">
        <h2>Coordination</h2>
        <label>Government Counterparts<textarea rows={2} placeholder="e.g. relevant state/local authorities" {...register('government_counterparts')} disabled={!canManage} /></label>
        <label>Cluster/Sector Coordination<textarea rows={2} {...register('cluster_sector_coordination')} disabled={!canManage} /></label>
        <label>Donor Coordination<textarea rows={2} {...register('donor_coordination')} disabled={!canManage} /></label>
        <label>Community Coordination<textarea rows={2} {...register('community_coordination')} disabled={!canManage} /></label>
        <label>Other Coordination<textarea rows={2} {...register('other_coordination')} disabled={!canManage} /></label>
      </section>
      <section className="card">
        <h2>Operational Considerations</h2>
        <label>Access Arrangements<textarea rows={2} {...register('access_arrangements')} disabled={!canManage} /></label>
        <label>Security Considerations<textarea rows={2} {...register('security_considerations')} disabled={!canManage} /></label>
        <label>Procurement Approach<textarea rows={2} {...register('procurement_approach')} disabled={!canManage} /></label>
        <label>Logistics Considerations<textarea rows={2} {...register('logistics_considerations')} disabled={!canManage} /></label>
        <label>Remote Management Considerations<textarea rows={2} {...register('remote_management_considerations')} disabled={!canManage} /></label>
      </section>
      <section className="card placeholder-card">
        <h2>Partners</h2>
        <p className="muted">Clean extension point for future Partner relationships - not built in this phase.</p>
      </section>
      {canManage && <div className="form-actions"><button type="submit" disabled={saveMutation.isPending}>{saveMutation.isPending ? 'Saving…' : 'Save Implementation'}</button></div>}
    </form>
  );
}
